import {
	ActionList,
	ActionListBusyIndicator,
	ActionListCheckbox,
	ActionListInfo,
	ActionListItem,
	ActionListSearch,
	ChangeTriggerKind,
	DropOver,
	FormItem,
	IconButton,
	MultiSelect,
	MultiSelectOption,
	Portal,
	ResponsiveMenu,
	SearchInput,
	Select,
	SelectOption,
	Toolbar
} from "client/Components";
import { ActionListCollapsibleSection } from "client/Components/ActionList/ActionListCollapsibleSection";
import { IInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { flattenChildren, getReactNodeKey } from "client/Misc/reactUtils";
import { localize } from "client_legacy/ndaw/culture";
import {
	createContext,
	Fragment,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	RefObject,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./SearchPage.module.less";
import { Guid } from "client_legacy/ndaw/guid";

export type SearchSettingsProps = {
	menuTarget: HTMLElement | null;
} & ({
	onClear?: undefined;
} | {
	onClear: () => void;
	hasFilter: boolean;
});

export const SearchSettings =(props: PropsWithChildren<SearchSettingsProps>): ReactElement | null => {
	const {
		menuTarget,
		onClear,
		children
	} = props;

	const [isDropOverOpen, setIsDropOverOpen] = useState<boolean>(false);
	const dropOverTargetRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const childrenArray = useMemo(
		(): readonly ReactNode[] => {
			return flattenChildren(children);
		},
		[children]
	);

	const closeHandler = useCallback(
		(): void => setIsDropOverOpen(false),
		[]
	);

	if (childrenArray.length === 0) {
		return null;
	}

	const trackingId: string = "search-settings";
	const menuTrackingId: string = generateTrackingId(trackingId, "--menu");

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				className={styles.searchSettings}
			>
				<ul className={styles.settings}>
					<SearchSettingsContext.Provider value="default">
						{childrenArray.map((x, i): ReactElement => (
							<li key={getReactNodeKey(x, i)}>
								{x}
							</li>
						))}
					</SearchSettingsContext.Provider>
				</ul>

				{onClear && (
					<div className={styles.clearFilter}>
						<IconButton
							kind="secondary"
							tooltip={localize("_ClearFilters")}
							onClick={onClear}
							isDisabled={!props.hasFilter}
							icon="filter-circle-xmark"
							trackingId="clear-filter"
						/>
					</div>
				)}
			</div>

			{menuTarget && (
				<Portal container={menuTarget}>
					<IconButton
						kind="secondary"
						icon="filter"
						trackingId={menuTrackingId}
						onClick={() => setIsDropOverOpen(true)}
						ref={dropOverTargetRef}
					/>

					{isDropOverOpen && dropOverTargetRef.current && (
						<DropOver
							target={dropOverTargetRef.current}
							contentAlignment="tr"
							targetAlignment="br"
							onClose={closeHandler}
						>
							<ResponsiveMenu
								onClose={closeHandler}
								trackingId={generateTrackingId(menuTrackingId, "__drop-over__wrapper")}
								height={onClear ? "fill" : undefined}
							>
								<ResponsiveMenu.Header
									title={localize("_SortAndFilter")}
								/>

								<ResponsiveMenu.Body goesEdgeToEdge>
									<ActionList
										trackingId={generateTrackingId(menuTrackingId, "__drop-over")}
									>
										<SearchSettingsContext.Provider value="menu">
											{childrenArray.map((x, i): ReactElement => (
												<Fragment key={getReactNodeKey(x, i)}>
													{x}
												</Fragment>
											))}
										</SearchSettingsContext.Provider>
									</ActionList>
								</ResponsiveMenu.Body>

								{onClear && (
									<ResponsiveMenu.Footer>
										<Toolbar>
											<Toolbar.Button
												trackingId="clear-filter"
												kind="transparent-for-list"
												text={localize("_ClearFilters")}
												onClick={onClear}
											/>

											<Toolbar.Button
												trackingId="close"
												kind="secondary"
												text={localize("_CloseMenu")}
												onClick={closeHandler}
											/>
										</Toolbar>
									</ResponsiveMenu.Footer>
								)}
							</ResponsiveMenu>
						</DropOver>
					)}
				</Portal>
			)}
		</>
	);
};
SearchSettings.displayName = "SearchSettings";

type SearchSettingsVariant = "default" | "menu";

const SearchSettingsContext = createContext<SearchSettingsVariant>("default");

type SearchSettingsSelectProps<TValue> = {
	label: string;
	attachable: IInputAttachable<TValue>;
	options: readonly SelectOption<TValue>[];
	isExpandedByDefault?: boolean;
	hasBusyIndicator?: boolean;
	placeholder?: string;
};

const SearchSettingsSelect = <TValue,>(props: SearchSettingsSelectProps<TValue>): ReactElement => {
	const {
		label,
		attachable,
		options,
		isExpandedByDefault = false,
		hasBusyIndicator = false,
		placeholder
	} = props;

	const variant: SearchSettingsVariant = useContext(SearchSettingsContext);

	const onChangeHandler = useCallback(
		(
			value: TValue
		): void => {
			attachable.reducer
				.setValue(value);

			attachable.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	if (variant === "default") {
		return (
			<FormItem
				label={label}
				isLabelEllipsed
				hasNoMargin
			>
				<Select
					{...attachable.attachToSelect()}
					options={options}
					areEqual={deepComparison}
					hasBusyIndicator={hasBusyIndicator}
					placeholder={placeholder}
					menuTitle={label}
				/>
			</FormItem>
		);
	}

	const trackingId: string = attachable.reducer
		.path;

	return (
		<ActionListCollapsibleSection
			header={label}
			trackingId={trackingId}
			isExpandedByDefault={isExpandedByDefault}
		>
			{options.map((x): ReactElement => {
				const itemTrackingValue: string = getItemValueTrackingId(x);
				const itemTrackingId: string = generateTrackingId(trackingId, `__item|${itemTrackingValue}`);
				const isSelected: boolean = deepComparison(attachable.reducer.value, x.value);

				return (
					<ActionListItem
						key={itemTrackingId}
						isActive={isSelected}
						rightIcon={isSelected ? "check" : undefined}
						text={x.text}
						onClick={() => onChangeHandler(x.value)}
						shouldNotAutoCloseContainingDropOver
						trackingId={itemTrackingId}
					/>
				);
			})}
			{hasBusyIndicator && (<ActionListBusyIndicator />)}
		</ActionListCollapsibleSection>
	);
};
SearchSettingsSelect.displayName = "SearchSettings.Select";

type SearchSettingsMultiSelectProps<TValue extends {}> = {
	label: string;
	attachable: IValueInputAttachable<readonly TValue[]>;
	options: readonly MultiSelectOption<TValue>[];
	placeholder?: string;
	hasBusyIndicator?: boolean;
	isExpandedByDefault?: boolean;
};

const SearchSettingsMultiSelect = <TValue extends {}>(props: SearchSettingsMultiSelectProps<TValue>): ReactElement => {
	const {
		label,
		attachable,
		options,
		placeholder,
		hasBusyIndicator,
		isExpandedByDefault = false
	} = props;

	const variant: SearchSettingsVariant = useContext(SearchSettingsContext);

	const onChangeHandler = useCallback(
		(
			checkedState: boolean,
			value: TValue
		): void => {
			const copy: TValue[] = [...attachable.reducer.value];
			if (checkedState) {
				copy.push(value);
			} else {
				const indexInValue: number = copy.indexOf(value);
				copy.splice(indexInValue, 1);
			}

			attachable.reducer
				.setValue(copy);

			attachable.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	if (variant === "default") {
		return (
			<FormItem
				label={label}
				isLabelEllipsed
				hasNoMargin
			>
				<MultiSelect
					{...attachable.attachToMultiSelect()}
					options={options}
					placeholder={placeholder}
					hasBusyIndicator={hasBusyIndicator}
					menuTitle={label}
				/>
			</FormItem>
		);
	}

	const trackingId: string = attachable.reducer
		.path;

	return (
		<ActionListCollapsibleSection
			header={label}
			trackingId={trackingId}
			isExpandedByDefault={isExpandedByDefault}
		>
			{(!hasBusyIndicator && options.length === 0) && (
				<ActionListInfo
					text={localize("_NoDataFound")}
					isItalic
					trackingId="no-data-was-found"
				/>
			)}
			{options.map((x): ReactElement => {
				const itemTrackingValue: string = getItemValueTrackingId(x);
				const itemTrackingId: string = generateTrackingId(trackingId, `__item|${itemTrackingValue}`);

				const value: readonly TValue[] = attachable.reducer
					.value;

				return (
					<ActionListCheckbox
						key={itemTrackingId}
						value={value.some((z) => deepComparison(z, x.value))}
						text={x.text}
						onChange={(checkedState) => onChangeHandler(
							checkedState,
							x.value
						)}
						shouldNotAutoCloseContainingDropOver
						trackingId={itemTrackingId}
					/>
				);
			})}
			{hasBusyIndicator && (<ActionListBusyIndicator />)}
		</ActionListCollapsibleSection>
	);
};
SearchSettingsMultiSelect.displayName = "SearchSettings.MultiSelect";

type SearchSettingsTextInputProps = {
	label: string;
	attachable: IValueInputAttachable<VersionedValue<string>>;
	placeholder?: string;
	isExpandedByDefault?: boolean;
};

const SearchSettingsTextInput = (props: SearchSettingsTextInputProps): ReactElement => {
	const {
		label,
		attachable,
		placeholder,
		isExpandedByDefault = false
	} = props;

	const variant: SearchSettingsVariant = useContext(SearchSettingsContext);

	const value: VersionedValue<string> = attachable.reducer
		.value;

	const onChange = (value: string, version: Guid) => {
		attachable.reducer
			.setValue({
				value,
				version
			});

		attachable.validation
			.triggerInputFinished();
	};

	if (variant === "default") {
		return (
			<FormItem label={label} hasNoMargin>
				<SearchInput
					trackingId={attachable.reducer.path}
					variant="white"
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					trigger={{ kind: ChangeTriggerKind.OnEnter }}
					icon="none"
				/>
			</FormItem>
		);
	}

	const trackingId: string = attachable.reducer
		.path;

	return (
		<ActionListCollapsibleSection
			header={label}
			trackingId={trackingId}
			isExpandedByDefault={isExpandedByDefault}
		>
			<ActionListSearch
				placeholder={placeholder}
				value={value}
				onChange={onChange}
				trigger={{ kind: ChangeTriggerKind.OnEnter }}
				icon="none"
				variant="white"
			/>
		</ActionListCollapsibleSection>
	);
};
SearchSettingsTextInput.displayName = "SearchSettings.TextInput";

SearchSettings.Select = SearchSettingsSelect;
SearchSettings.MultiSelect = SearchSettingsMultiSelect;
SearchSettings.TextInput = SearchSettingsTextInput;

function getItemValueTrackingId(item: SelectOption<unknown>): string {
	if (isNotNilOrEmpty(item.valueTrackingId)) {
		return item.valueTrackingId;
	}

	return convertArbitraryValueForTrackingId(item.value);
}
